import { useQueryClient } from '@tanstack/react-query';
import {
  CompanyOnboardingStepKey,
  putV1CompaniesCompanyIdOnboardingStepsStepKeyComplete,
  putV1CompaniesCompanyIdOnboardingStepsStepKeyReopen,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useCallback } from 'react';

import { CompanyOnboardingStep } from '~hooks/useCompanyOnboarding/models';
import { COMPANY_ONBOARDING_PROGRESS_QUERY_KEY } from '~queries/useCompanyOnboardingProgressQuery';
import reportRequestError from '~services/reportRequestError';

export default function useCompanyOnboarding() {
  const queryClient = useQueryClient();

  const toggleStep = useCallback(
    async (companyId: string, stepKey: CompanyOnboardingStepKey) => {
      const queryKey = [COMPANY_ONBOARDING_PROGRESS_QUERY_KEY, companyId];
      const path = { 'company-id': companyId, 'step-key': stepKey };
      const previousSteps = queryClient.getQueryData<CompanyOnboardingStep[]>(queryKey);

      if (!previousSteps) {
        return;
      }

      const isCompleted = previousSteps.some((step) => step.stepKey === stepKey);

      try {
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData(
          queryKey,
          isCompleted
            ? previousSteps.filter((step) => step.stepKey !== stepKey)
            : [
                ...previousSteps,
                new CompanyOnboardingStep(`optimistic-${stepKey}`, stepKey, dayjs()),
              ],
        );

        if (isCompleted) {
          await putV1CompaniesCompanyIdOnboardingStepsStepKeyReopen({ path });
        } else {
          await putV1CompaniesCompanyIdOnboardingStepsStepKeyComplete({ path });
        }
      } catch (error) {
        const previousStep = previousSteps.find((step) => step.stepKey === stepKey);
        queryClient.setQueryData<CompanyOnboardingStep[]>(queryKey, (steps = []) => [
          ...steps.filter((step) => step.stepKey !== stepKey),
          ...(previousStep ? [previousStep] : []),
        ]);
        reportRequestError(
          error,
          isCompleted
            ? t('error_messages.company_onboarding.failed_to_reopen_step')
            : t('error_messages.company_onboarding.failed_to_complete_step'),
        );
      } finally {
        await queryClient.invalidateQueries({ queryKey });
      }
    },
    [queryClient],
  );

  return { toggleStep };
}
